import { useQuery } from "@tanstack/react-query";
import { getFaucetStatus } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, Gauge } from "lucide-react";

const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

const TokenReserve = ({ token, info }) => {
  const { balance, lowThreshold, isLow, isEmpty, isDemo } = info;
  const pct = balance == null ? 0 : Math.min(100, (balance / (lowThreshold * 10)) * 100);
  const id = token.toLowerCase();
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p data-testid={`faucet-balance-${id}`} className="font-display text-xl font-bold text-white">
          {balance == null ? "Unavailable" : fmt(balance)} <span className="text-[#D4AF37] text-sm">{token}</span>
        </p>
        <Badge className={isDemo ? "demo-badge" : "bg-green-500/10 text-green-400 border border-green-500/30"}>
          {isDemo ? "Demo" : "Live"}
        </Badge>
      </div>
      <Progress value={pct} className={`h-1.5 bg-[#1A1708] ${isLow ? "[&>div]:bg-red-500" : "[&>div]:bg-[#D4AF37]"}`} />
      {isLow && (
        <div data-testid={`faucet-low-warning-${id}`} className="flex items-start gap-2 p-2.5 rounded-md bg-red-500/5 border border-red-500/20 text-red-400 text-xs">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          {isEmpty
            ? `${token} faucet is empty. Requests are paused until it is refilled.`
            : `${token} faucet is running low (below ${fmt(lowThreshold)} ${token}). Please refill soon.`}
        </div>
      )}
    </div>
  );
};

export const FaucetMonitor = () => {
  const { data } = useQuery({ queryKey: ["faucetStatus"], queryFn: getFaucetStatus, refetchInterval: 20000 });
  if (!data) return null;
  return (
    <div data-testid="faucet-monitor" className="p-4 rounded-lg bg-[#050505] border border-[#2A2411] space-y-4">
      <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider">
        <Gauge size={14} className="text-[#D4AF37]" /> Faucet Reserve
      </div>
      {Object.entries(data.tokens).map(([token, info]) => <TokenReserve key={token} token={token} info={info} />)}
      {data.faucetAddress && (
        <p data-testid="faucet-wallet-address" className="font-mono text-[11px] text-zinc-600 break-all">Faucet wallet: {data.faucetAddress}</p>
      )}
    </div>
  );
};
